import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { 
  HelpCircle, Heart, Users, MessageSquare, FileText, 
  Shield, CreditCard, Search, CheckCircle, ArrowRight,
  Lightbulb, Target, Zap
} from 'lucide-react';

export default function Help() {
  const { isDark } = useTheme();
  const { user } = useAuth();
  const [activeSection, setActiveSection] = useState('getting-started');

  // Theme classes
  const bg = isDark ? 'bg-slate-900' : 'bg-slate-100';
  const cardBg = isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200';
  const textPrimary = isDark ? 'text-white' : 'text-blue-900';
  const textSecondary = isDark ? 'text-slate-300' : 'text-blue-700';
  const accordionBg = isDark ? 'bg-slate-700' : 'bg-slate-50';
  const accordionBorder = isDark ? 'border-slate-600' : 'border-slate-200';
  const badgeBg = isDark ? 'bg-slate-700 text-blue-300' : 'bg-blue-100 text-blue-800';

  const sections = [
    { id: 'getting-started', label: 'Getting Started', icon: Lightbulb },
    { id: 'matching', label: 'How Matching Works', icon: Heart },
    { id: 'messaging', label: 'Messaging', icon: MessageSquare },
    { id: 'profiles', label: 'Profiles & Discovery', icon: Search },
    { id: 'subscriptions', label: 'Subscriptions', icon: CreditCard },
  ];

  return (
    <div className={`min-h-screen ${bg}`} data-testid="help-page">
      <Navigation />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <HelpCircle className="text-blue-500" size={32} />
            <h1 className={`text-3xl font-bold ${textPrimary}`}>Help Center</h1>
          </div>
          <p className={textSecondary}>Learn how to make the most of SeedSynch</p>
        </div>

        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <Card className={`p-4 ${cardBg}`}>
              <h3 className={`text-sm font-semibold mb-3 ${textPrimary}`}>Topics</h3>
              <nav className="space-y-1">
                {sections.map(section => {
                  const Icon = section.icon;
                  return (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                        activeSection === section.id
                          ? 'bg-blue-500 text-white'
                          : isDark 
                            ? 'text-slate-300 hover:bg-slate-600' 
                            : 'text-blue-800 hover:bg-slate-100'
                      }`}
                    >
                      <Icon size={16} />
                      {section.label}
                    </button>
                  );
                })}
              </nav>
            </Card>
          </div>

          {/* Content Area */}
          <div className="lg:col-span-3">
            {/* Getting Started */}
            {activeSection === 'getting-started' && (
              <Card className={`p-6 ${cardBg}`}>
                <h2 className={`text-xl font-bold mb-4 flex items-center gap-2 ${textPrimary}`}>
                  <Lightbulb className="text-blue-500" size={24} />
                  Getting Started
                </h2>
                
                <div className={`p-4 rounded-lg mb-6 ${isDark ? 'bg-blue-900/30 border border-blue-700' : 'bg-blue-50 border border-blue-100'}`}>
                  <h3 className={`font-semibold mb-2 ${textPrimary}`}>Welcome to SeedSynch!</h3>
                  <p className={`text-sm ${textSecondary}`}>
                    SeedSynch is a matchmaking platform that connects Shariah-conscious founders, investors, and co-founders. 
                    We help you find partners with compatible expectations before any capital discussions.
                  </p>
                </div>

                <div className="space-y-4">
                  <div className={`p-4 rounded-lg border ${accordionBorder} ${accordionBg}`}>
                    <h4 className={`font-semibold mb-2 ${textPrimary}`}>Step 1: Complete Your Profile</h4>
                    <p className={`text-sm ${textSecondary}`}>
                      A complete profile helps our matching algorithm find the best partners for you. 
                      Include your industry focus, stage preferences, and funding criteria.
                    </p>
                  </div>
                  
                  <div className={`p-4 rounded-lg border ${accordionBorder} ${accordionBg}`}>
                    <h4 className={`font-semibold mb-2 ${textPrimary}`}>Step 2: Browse & Discover</h4>
                    <p className={`text-sm ${textSecondary}`}>
                      Go to the <strong>Discover</strong> page to see profiles matched to your criteria. 
                      Each profile shows a match score breakdown so you know why they're a good fit.
                    </p>
                  </div>
                  
                  <div className={`p-4 rounded-lg border ${accordionBorder} ${accordionBg}`}>
                    <h4 className={`font-semibold mb-2 ${textPrimary}`}>Step 3: Express Interest</h4>
                    <p className={`text-sm ${textSecondary}`}>
                      When you find someone interesting, click the <strong>❤️ Like</strong> button. 
                      If they like you back, you're matched and can start messaging!
                    </p>
                  </div>
                  
                  <div className={`p-4 rounded-lg border ${accordionBorder} ${accordionBg}`}>
                    <h4 className={`font-semibold mb-2 ${textPrimary}`}>Step 4: Connect & Chat</h4>
                    <p className={`text-sm ${textSecondary}`}>
                      Once matched, use the messaging feature to start conversations. 
                      Share your pitch deck (with NDA protection) and discuss potential partnerships.
                    </p>
                  </div>
                </div>
              </Card>
            )}

            {/* How Matching Works */}
            {activeSection === 'matching' && (
              <Card className={`p-6 ${cardBg}`}>
                <h2 className={`text-xl font-bold mb-4 flex items-center gap-2 ${textPrimary}`}>
                  <Heart className="text-blue-500" size={24} />
                  How Matching Works
                </h2>

                <div className={`p-4 rounded-lg mb-6 ${isDark ? 'bg-green-900/30 border border-green-700' : 'bg-green-50 border border-green-100'}`}>
                  <h3 className={`font-semibold mb-2 ${textPrimary}`}>🎯 Two-Way Matching System</h3>
                  <p className={`text-sm ${textSecondary}`}>
                    SeedSynch uses a mutual interest system. Both parties must express interest before messaging is enabled. 
                    This ensures conversations start with genuine mutual interest.
                  </p>
                </div>

                <Accordion type="single" collapsible className="space-y-2">
                  <AccordionItem value="how-to-match" className={`border rounded-lg ${accordionBorder}`}>
                    <AccordionTrigger className={`px-4 ${textPrimary}`}>How do I match with someone?</AccordionTrigger>
                    <AccordionContent className={`px-4 pb-4 ${textSecondary}`}>
                      <ol className="list-decimal list-inside space-y-2 text-sm">
                        <li>Go to the <strong>Discover</strong> page</li>
                        <li>Browse profiles - each shows a compatibility score</li>
                        <li>Click the <strong>❤️ Like</strong> button on profiles you're interested in</li>
                        <li>If they also like you, you're matched!</li>
                        <li>Go to <strong>Matches</strong> to see all your mutual matches</li>
                        <li>Click on a match to start messaging</li>
                      </ol>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="match-score" className={`border rounded-lg ${accordionBorder}`}>
                    <AccordionTrigger className={`px-4 ${textPrimary}`}>What does the match score mean?</AccordionTrigger>
                    <AccordionContent className={`px-4 pb-4 ${textSecondary}`}>
                      <p className="text-sm mb-3">The match score (0-100%) is calculated based on 4 factors:</p>
                      <ul className="space-y-2 text-sm">
                        <li className="flex items-start gap-2">
                          <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs font-medium">40%</span>
                          <span><strong>Industry Fit</strong> - Overlap in industry focus areas</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs font-medium">30%</span>
                          <span><strong>Stage Match</strong> - Alignment on startup stage (Pre-seed, Seed, etc.)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs font-medium">20%</span>
                          <span><strong>Funding Fit</strong> - Compatible funding/ticket size ranges</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs font-medium">10%</span>
                          <span><strong>Location</strong> - Geographic alignment</span>
                        </li>
                      </ul>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="why-match-first" className={`border rounded-lg ${accordionBorder}`}>
                    <AccordionTrigger className={`px-4 ${textPrimary}`}>Why do I need to match before messaging?</AccordionTrigger>
                    <AccordionContent className={`px-4 pb-4 ${textSecondary}`}>
                      <p className="text-sm">
                        The two-way matching system ensures that conversations only happen when there's mutual interest. 
                        This reduces noise, protects users from unsolicited messages, and makes every conversation more 
                        meaningful since both parties have already expressed interest.
                      </p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="see-who-liked" className={`border rounded-lg ${accordionBorder}`}>
                    <AccordionTrigger className={`px-4 ${textPrimary}`}>Can I see who liked me?</AccordionTrigger>
                    <AccordionContent className={`px-4 pb-4 ${textSecondary}`}>
                      <p className="text-sm">
                        On paid plans, you can see when someone has expressed interest in your profile. 
                        Free users see matches only after mutual interest is confirmed.
                      </p>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </Card>
            )}

            {/* Messaging */}
            {activeSection === 'messaging' && (
              <Card className={`p-6 ${cardBg}`}>
                <h2 className={`text-xl font-bold mb-4 flex items-center gap-2 ${textPrimary}`}>
                  <MessageSquare className="text-blue-500" size={24} />
                  Messaging
                </h2>

                <Accordion type="single" collapsible className="space-y-2">
                  <AccordionItem value="start-chat" className={`border rounded-lg ${accordionBorder}`}>
                    <AccordionTrigger className={`px-4 ${textPrimary}`}>How do I start a conversation?</AccordionTrigger>
                    <AccordionContent className={`px-4 pb-4 ${textSecondary}`}>
                      <ol className="list-decimal list-inside space-y-2 text-sm">
                        <li>First, you need to match with someone (see "How Matching Works")</li>
                        <li>Once matched, go to the <strong>Matches</strong> page</li>
                        <li>Click on the match you want to message</li>
                        <li>Type your message and hit send!</li>
                      </ol>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="share-files" className={`border rounded-lg ${accordionBorder}`}>
                    <AccordionTrigger className={`px-4 ${textPrimary}`}>Can I share files in messages?</AccordionTrigger>
                    <AccordionContent className={`px-4 pb-4 ${textSecondary}`}>
                      <p className="text-sm">
                        Yes! Paid plan users can share files (PDFs, documents) directly in the chat. 
                        For sensitive documents like pitch decks, we recommend using the NDA-protected pitch deck feature instead.
                      </p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="nda" className={`border rounded-lg ${accordionBorder}`}>
                    <AccordionTrigger className={`px-4 ${textPrimary}`}>How does NDA-protected sharing work?</AccordionTrigger>
                    <AccordionContent className={`px-4 pb-4 ${textSecondary}`}>
                      <p className="text-sm">
                        Founders can upload pitch decks that are only accessible after an investor accepts the NDA. 
                        All NDA acceptances are logged with timestamps for both parties' records.
                      </p>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </Card>
            )}

            {/* Profiles & Discovery */}
            {activeSection === 'profiles' && (
              <Card className={`p-6 ${cardBg}`}>
                <h2 className={`text-xl font-bold mb-4 flex items-center gap-2 ${textPrimary}`}>
                  <Search className="text-blue-500" size={24} />
                  Profiles & Discovery
                </h2>

                <Accordion type="single" collapsible className="space-y-2">
                  <AccordionItem value="improve-score" className={`border rounded-lg ${accordionBorder}`}>
                    <AccordionTrigger className={`px-4 ${textPrimary}`}>How can I improve my match scores?</AccordionTrigger>
                    <AccordionContent className={`px-4 pb-4 ${textSecondary}`}>
                      <ul className="list-disc list-inside space-y-2 text-sm">
                        <li>Complete your profile with accurate industry preferences</li>
                        <li>Be specific about your stage and funding requirements</li>
                        <li>Add your geographic focus areas</li>
                        <li>Keep your profile information up to date</li>
                      </ul>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="visibility" className={`border rounded-lg ${accordionBorder}`}>
                    <AccordionTrigger className={`px-4 ${textPrimary}`}>Who can see my profile?</AccordionTrigger>
                    <AccordionContent className={`px-4 pb-4 ${textSecondary}`}>
                      <p className="text-sm">
                        Your profile is visible to other SeedSynch users based on their role. 
                        Founders see investors, investors see founders, and co-founders see relevant opportunities. 
                        Sensitive information (like detailed financials) is only shared after NDA acceptance.
                      </p>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </Card>
            )}

            {/* Subscriptions */}
            {activeSection === 'subscriptions' && (
              <Card className={`p-6 ${cardBg}`}>
                <h2 className={`text-xl font-bold mb-4 flex items-center gap-2 ${textPrimary}`}>
                  <CreditCard className="text-blue-500" size={24} />
                  Subscriptions
                </h2>

                <Accordion type="single" collapsible className="space-y-2">
                  <AccordionItem value="free-vs-paid" className={`border rounded-lg ${accordionBorder}`}>
                    <AccordionTrigger className={`px-4 ${textPrimary}`}>What's the difference between Free and Paid plans?</AccordionTrigger>
                    <AccordionContent className={`px-4 pb-4 ${textSecondary}`}>
                      <p className="text-sm mb-3">Free plans let you explore the platform:</p>
                      <ul className="list-disc list-inside space-y-1 text-sm mb-3">
                        <li>Create your profile</li>
                        <li>Limited browsing (3-10 profiles/month)</li>
                        <li>Basic matching</li>
                      </ul>
                      <p className="text-sm mb-3">Paid plans unlock full access:</p>
                      <ul className="list-disc list-inside space-y-1 text-sm">
                        <li>Unlimited discovery</li>
                        <li>Direct messaging</li>
                        <li>NDA-protected pitch deck access</li>
                        <li>Advanced filters</li>
                        <li>Priority support</li>
                      </ul>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="change-plan" className={`border rounded-lg ${accordionBorder}`}>
                    <AccordionTrigger className={`px-4 ${textPrimary}`}>How do I upgrade or change my plan?</AccordionTrigger>
                    <AccordionContent className={`px-4 pb-4 ${textSecondary}`}>
                      <p className="text-sm">
                        Go to <strong>Account → Subscription</strong> and click "Change Plan". 
                        You'll see a comparison of what features you'll gain or lose. 
                        Upgrades take effect immediately; downgrades take effect at the end of your billing period.
                      </p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="cancel" className={`border rounded-lg ${accordionBorder}`}>
                    <AccordionTrigger className={`px-4 ${textPrimary}`}>Can I cancel my subscription?</AccordionTrigger>
                    <AccordionContent className={`px-4 pb-4 ${textSecondary}`}>
                      <p className="text-sm">
                        Yes, you can cancel anytime. Your subscription will remain active until the end of 
                        your current billing period, then revert to the Free tier.
                      </p>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </Card>
            )}

            {/* Quick Links */}
            <Card className={`p-4 mt-6 ${cardBg}`}>
              <h3 className={`font-semibold mb-3 ${textPrimary}`}>Quick Links</h3>
              <div className="flex flex-wrap gap-2">
                <Link to="/discovery">
                  <Button size="sm" className={isDark ? 'btn-secondary-dark' : 'btn-secondary'}>
                    <Search size={14} className="mr-1" /> Discover
                  </Button>
                </Link>
                <Link to="/matches">
                  <Button size="sm" className={isDark ? 'btn-secondary-dark' : 'btn-secondary'}>
                    <Users size={14} className="mr-1" /> Matches
                  </Button>
                </Link>
                <Link to="/account">
                  <Button size="sm" className={isDark ? 'btn-secondary-dark' : 'btn-secondary'}>
                    <CreditCard size={14} className="mr-1" /> Account
                  </Button>
                </Link>
                <Link to="/faq">
                  <Button size="sm" className={isDark ? 'btn-secondary-dark' : 'btn-secondary'}>
                    <HelpCircle size={14} className="mr-1" /> Public FAQ
                  </Button>
                </Link>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
